/**
 * Tasks — the "My day" page's rules (Tasks redesign Phase B0, 2026-10-06).
 *
 * Plan: C:\Users\nilay\.claude\plans\eager-painting-hejlsberg.md — "Tasks 2a" and "Phase B — B0".
 *
 * ⛔ KEEP THIS FILE IMPORT-FREE AND CLOCK-FREE (the node-tests execute it verbatim). Everything that
 *    needs moment, the DOM, the store or "now" is computed by the caller and passed in `ctx`.
 *
 * ── WHAT IT IS ─────────────────────────────────────────────────────────────────────────────────
 * The redesigned /app/todo (design "Tasks 2a") replaces ToDo.vue's filter bar with VIEWS (My day ·
 * Assigned to me · Created by me · Upcoming · Everyone · Completed today), a Team / Tags sidebar
 * showing the busiest few, a progress ring, an overdue "catch-up" tray (Move all to today, Review)
 * and the sections Today / Coming up / Done today. Every list and number on that page comes from
 * the functions below, fed by the SAME store lists the old page reads (src/store/tasks.js):
 *   input = { myCreated, assignedToMe, myEmployees, allSite, completedSite }
 *         = state.tasks.{ myCreatedTasks, assignedToMeTasks, myEmployeesTasks, allTasksForSite,
 *                         completedTasksForSite }
 *   ctx   = { userID, today: 'YYYY-MM-DD', timeZone, isAdmin, now?, clean? }
 *     today     moment().format('YYYY-MM-DD') — main.js sets moment's default zone to
 *               America/New_York, and that is the "day" the store getter and ToDo.vue compare to.
 *     timeZone  the office zone (GENERAL_TIMEZONE, default America/New_York as TodoDetails does).
 *               Used for "Move all to today" and for days of Date / Timestamp values.
 *     now       optional Date — only stamps `lastActivity` on the Move-all patches.
 *     clean     optional (content) => { text, patients, tags, employees } — pass
 *               c => cleanTaskContent(c, document) (completeTask.js) for exact DOM parsing; without
 *               it parseTaskContent below (regex, agreement-tested against the DOM version) is used.
 *
 * The OLD page (ToDo.vue, TodoItem.vue, TodoDetails.vue) stays untouched and keeps serving everyone
 * whose uiVersion.js STAGE.tasks / office choice says classic (gate key: `tasks`).
 *
 * ── THE AGREEMENTS (cypress/node-tests/tasks-my-day-rules.test.js) ─────────────────────────────
 * • My day == store getter `todaysTasksForEmployee(userID)` (store/tasks.js), quirks and all:
 *     (createdByEmpID === me || assignedToEmpID === me) && status === 'INCOMPLETE' && (
 *       moment(dueAtStr) is today || Daily || Weekly on today's 'ddd' || Bi-Weekly on the 'ddd' of
 *       today + 2 weeks || Monthly on today's date || Yearly on month+day )
 *   Mirrored quirks (Nilay decides later — do NOT fix here without changing the getter):
 *     1. `assignees[]` is ignored — only assignedToEmpID (TodoDetails saves `assignees`, not
 *        assignedToEmpID, so a reassigned task stays in the ORIGINAL assignee's My day).
 *     2. A task with NO dueAtStr key is "due today" every day (moment(undefined) is now).
 *        null / '' never are.
 *     3. Bi-Weekly compares repeatOn to the weekday of today + 2 weeks = today's weekday, so it
 *        matches EVERY week, like Weekly.
 *     4. Weekly / Bi-Weekly compare to moment 'ddd' ('Thu'); TodoDetails' picker stores 'Thurs',
 *        so a Thursday task picked there never matches by rule (only via its dueAtStr).
 *     5. Monthly compares repeatOnDay === date with ===, so a string '6' never matches; a
 *        "last day of month" task (no repeatOnDay) only matches via its dueAtStr.
 *     6. Yearly compares repeatOnMonth to `moment().month+1` (a function + 1 → a string): never
 *        true, so a Yearly task only matches via its dueAtStr.
 *     7. dueAtStr is parsed as MM/DD/YYYY even for AFRICA/MASERU offices, which save DD/MM/YYYY.
 * • Overdue == ToDo.vue's "Show overdue tasks only" filter (also TodoItem `isOverdue`):
 *     status === 'INCOMPLETE' && moment(dueAt).isBefore(moment(), 'day')
 *   It reads `dueAt` (the store's 'MM/DD/YYYY h:mm:ss a' string, browser-local), not dueAtStr.
 *   Repeating tasks ARE overdue here, as on the old page (the nightly reset normally rolls them
 *   first). sections() puts a task that is also due today (e.g. Daily) in Today, never in both.
 * • Move all to today == TodoDetails' save: dueAt = moment.tz(day, officeTimezone).startOf('day')
 *   .toDate(), dueAtStr = that day as 'MM/DD/YYYY' ('DD/MM/YYYY' for AFRICA/MASERU). The patch
 *   ALSO carries the task's own `tags`, because tasks/updateTask writes `tags: []` when a payload
 *   has none. Repeating tasks are NOT moved (moving one re-anchors its whole series) — the tray
 *   still lists and counts them.
 *
 * Days are compared as 'YYYY-MM-DD' strings. Store strings ('MM/DD/YYYY…') are read as the
 * calendar date they spell; Date / Firestore Timestamp / number values are zoned to ctx.timeZone.
 */

export const DEFAULT_TIME_ZONE = 'America/New_York';

/** The sidebar views, in order. */
export const VIEWS = [
  { key: 'myDay', label: 'My day' },
  { key: 'assignedToMe', label: 'Assigned to me' },
  { key: 'createdByMe', label: 'Created by me' },
  { key: 'upcoming', label: 'Upcoming' },
  { key: 'everyone', label: 'Everyone' },
  { key: 'completedToday', label: 'Completed today' },
];
export const VIEW_KEYS = VIEWS.map(v => v.key);

/** "Coming up" / Upcoming = due in the next 7 days; the My day section shows the first 3. */
export const COMING_UP_DAYS = 7;
export const COMING_UP_SHOWN = 3;
/** Sidebar: you + the 4 busiest teammates; the 6 most used tags. */
export const TEAM_SHOWN = 5;
export const TAGS_SHOWN = 6;

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DAY_NAMES_FULL = { Sun: 'Sunday', Mon: 'Monday', Tue: 'Tuesday', Wed: 'Wednesday', Thu: 'Thursday', Fri: 'Friday', Sat: 'Saturday' };
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const arr = v => (Array.isArray(v) ? v : []);
const isObj = v => !!v && typeof v === 'object';
const pad2 = n => (n < 10 ? '0' : '') + n;

// ---- Days ------------------------------------------------------------------------------------

const YMD_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const US_RE = /^\s*(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?\s*([ap]m)?)?/i;

function ymdOfUtc(ms) {
  const d = new Date(ms);
  return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
}

function partsOfYmd(ymd) {
  const m = typeof ymd === 'string' ? YMD_RE.exec(ymd) : null;
  return m ? { y: Number(m[1]), m: Number(m[2]), d: Number(m[3]) } : null;
}

/** 'YYYY-MM-DD' + n days (n may be negative), or null. */
export function addDays(ymd, n) {
  const p = partsOfYmd(ymd);
  if (!p) return null;
  return ymdOfUtc(Date.UTC(p.y, p.m - 1, p.d + (Number(n) || 0)));
}

/** 'Sun'…'Sat' (moment 'ddd') of a 'YYYY-MM-DD', or null. */
export function weekdayOf(ymd) {
  const p = partsOfYmd(ymd);
  if (!p) return null;
  return WEEKDAYS[new Date(Date.UTC(p.y, p.m - 1, p.d)).getUTCDay()];
}

const FORMATTERS = {};
function formatterFor(tz) {
  if (!FORMATTERS[tz]) {
    FORMATTERS[tz] = new Intl.DateTimeFormat('en-US', {
      timeZone: tz, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
  }
  return FORMATTERS[tz];
}

/** The wall clock of instant `ms` in `tz` → { y, m, d, h, mi, s }, or null. */
function wallClock(ms, tz) {
  const d = new Date(ms);
  if (Number.isNaN(d.getTime())) return null;
  let f;
  try {
    f = formatterFor(tz || DEFAULT_TIME_ZONE);
  } catch (e) {
    f = formatterFor(DEFAULT_TIME_ZONE);
  }
  const p = {};
  f.formatToParts(d).forEach((x) => { p[x.type] = x.value; });
  return { y: Number(p.year), m: Number(p.month), d: Number(p.day), h: Number(p.hour) % 24, mi: Number(p.minute), s: Number(p.second) };
}

function zoned(ms, tz) {
  const w = wallClock(ms, tz);
  return w ? { ymd: `${w.y}-${pad2(w.m)}-${pad2(w.d)}`, minutes: w.h * 60 + w.mi } : null;
}

/** 'M/D/YYYY[ h:mm[:ss][ am|pm]…]' as the calendar date it spells (out-of-range days roll, like V8's Date). */
function parseUs(str) {
  const m = US_RE.exec(str);
  if (!m) return null;
  const mo = Number(m[1]);
  const da = Number(m[2]);
  if (mo < 1 || mo > 12 || da < 1 || da > 31) return null;
  let minutes = null;
  if (m[4] !== undefined) {
    let h = Number(m[4]);
    if (m[7]) h = (h % 12) + (m[7].toLowerCase() === 'pm' ? 12 : 0);
    minutes = h * 60 + Number(m[5]);
  }
  return { ymd: ymdOfUtc(Date.UTC(Number(m[3]), mo - 1, da)), minutes };
}

/**
 * The day (and minutes into it, when known) that a task date value falls on, or null.
 * `undefinedIsNow` mirrors moment(undefined) === now (used where the old code calls moment(x)).
 */
function dateOf(value, ctx, undefinedIsNow) {
  if (value === undefined) return undefinedIsNow ? { ymd: todayOf(ctx), minutes: null } : null;
  if (value === null || value === '') return null;
  const tz = (ctx && ctx.timeZone) || DEFAULT_TIME_ZONE;
  if (value instanceof Date) return zoned(value.getTime(), tz);
  if (typeof value === 'number') return Number.isFinite(value) ? zoned(value, tz) : null;
  if (isObj(value)) {
    if (typeof value.toDate === 'function') {
      try {
        const d = value.toDate();
        return d instanceof Date ? zoned(d.getTime(), tz) : null;
      } catch (e) {
        return null;
      }
    }
    if (typeof value.seconds === 'number') return zoned(value.seconds * 1000, tz);
    return null;
  }
  if (typeof value !== 'string') return null;
  const ymd = YMD_RE.exec(value.trim());
  if (ymd) return { ymd: value.trim(), minutes: null };
  const us = parseUs(value);
  if (us) return us;
  if (US_RE.test(value)) return null; // M/D/YYYY out of range: moment's Date fallback is invalid too
  const ms = Date.parse(value);
  return Number.isNaN(ms) ? null : zoned(ms, tz);
}

function dayOf(value, ctx, undefinedIsNow) {
  const r = dateOf(value, ctx, undefinedIsNow);
  return r ? r.ymd : null;
}

/** ctx.today as 'YYYY-MM-DD' (a Date is zoned to ctx.timeZone), or null. */
export function todayOf(ctx) {
  if (!ctx) return null;
  if (typeof ctx.today === 'string' && YMD_RE.test(ctx.today)) return ctx.today;
  if (ctx.today instanceof Date) {
    const z = zoned(ctx.today.getTime(), ctx.timeZone || DEFAULT_TIME_ZONE);
    return z ? z.ymd : null;
  }
  return null;
}

/** Minutes east of UTC that `tz` is at instant `ms`. */
function offsetMinutes(ms, tz) {
  const w = wallClock(ms, tz);
  if (!w) return 0;
  const asUtc = Date.UTC(w.y, w.m - 1, w.d, w.h, w.mi, w.s);
  return Math.round((asUtc - (ms - (((ms % 1000) + 1000) % 1000))) / 60000);
}

/**
 * The instant `ymd` starts in `tz` (moment.tz(ymd, tz).startOf('day')) as epoch ms, or null.
 * A day whose midnight is skipped by DST starts at its first existing minute, as moment's does.
 */
export function startOfDayMs(ymd, tz) {
  const p = partsOfYmd(ymd);
  if (!p) return null;
  const zone = tz || DEFAULT_TIME_ZONE;
  const wallMs = Date.UTC(p.y, p.m - 1, p.d);
  let ms = wallMs - offsetMinutes(wallMs, zone) * 60000;
  ms = wallMs - offsetMinutes(ms, zone) * 60000;
  const z = zoned(ms, zone);
  if (z && z.ymd < ymd) {
    // midnight does not exist (spring-forward at 00:00) — step to the first minute of the day
    while (zoned(ms, zone).ymd < ymd) ms += 60000 * 15;
    while (zoned(ms - 60000, zone).ymd === ymd) ms -= 60000;
  } else if (z && z.ymd === ymd && z.minutes > 0) {
    ms -= z.minutes * 60000;
    if (zoned(ms, zone).ymd !== ymd) ms += z.minutes * 60000;
  }
  return ms;
}

/** TodoDetails' dueAtStr format for the office zone. */
export function dueAtStrFormat(tz) {
  return String(tz || DEFAULT_TIME_ZONE).toUpperCase() === 'AFRICA/MASERU' ? 'DD/MM/YYYY' : 'MM/DD/YYYY';
}

function formatYmd(ymd, format) {
  const p = partsOfYmd(ymd);
  if (!p) return '';
  return format === 'DD/MM/YYYY' ? `${pad2(p.d)}/${pad2(p.m)}/${p.y}` : `${pad2(p.m)}/${pad2(p.d)}/${p.y}`;
}

// ---- Task predicates -------------------------------------------------------------------------

/** status === 'INCOMPLETE' (the store's open lists are fetched with exactly this). */
export function isOpen(task) {
  return isObj(task) && task.status === 'INCOMPLETE';
}

/** The getter's ownership: created by me, or assignedToEmpID is me (assignees[] ignored — quirk 1). */
export function isMine(task, userID) {
  return isObj(task) && userID != null && (task.createdByEmpID === userID || task.assignedToEmpID === userID);
}

/** Every assignee: assignees[] ∪ assignedToEmpID (ToDo.vue's assignee filter), deduped as strings. */
export function assigneeIDs(task) {
  if (!isObj(task)) return [];
  const out = [];
  const seen = {};
  const add = (id) => {
    if (id === undefined || id === null || id === '') return;
    const k = String(id);
    if (!seen[k]) { seen[k] = true; out.push(id); }
  };
  arr(task.assignees).forEach(add);
  add(task.assignedToEmpID);
  return out;
}

/** repeat flag OR a real repeatFrequency (the getter reads repeatFrequency alone). */
export function isRepeating(task) {
  if (!isObj(task)) return false;
  const f = task.repeatFrequency;
  return !!task.repeat || (typeof f === 'string' && f !== '' && f !== 'Never');
}

/**
 * The date clause of store getter `todaysTasksForEmployee` (ownership and status are NOT checked
 * here — see isMyDayTask). Quirks 2–7 in the header are kept on purpose.
 */
export function isDueToday(task, ctx) {
  const today = todayOf(ctx);
  if (!isObj(task) || !today) return false;
  if (dayOf(task.dueAtStr, ctx, true) === today) return true;
  const f = task.repeatFrequency;
  if (f === 'Daily') return true;
  if (f === 'Weekly') return task.repeatOn === weekdayOf(today);
  if (f === 'Bi-Weekly') return task.repeatOn === weekdayOf(addDays(today, 14));
  if (f === 'Monthly') return task.repeatOnDay === partsOfYmd(today).d;
  // Yearly: `t.repeatOnMonth === moment().month+1` is never true (quirk 6).
  return false;
}

/** One row of the getter: mine && INCOMPLETE && due today. */
export function isMyDayTask(task, ctx) {
  return isMine(task, ctx && ctx.userID) && isOpen(task) && isDueToday(task, ctx);
}

/** ToDo.vue's overdue filter: INCOMPLETE && moment(dueAt).isBefore(moment(), 'day'). */
export function isOverdue(task, ctx) {
  const today = todayOf(ctx);
  if (!isOpen(task) || !today) return false;
  const due = dayOf(task.dueAt, ctx, true);
  return !!due && due < today;
}

/** The day the row shows as its due date: dueAtStr, else dueAt; null when neither. */
export function dueDayOf(task, ctx) {
  if (!isObj(task)) return null;
  return dayOf(task.dueAtStr, ctx, false) || dayOf(task.dueAt, ctx, false);
}

/** Open, not due today, not overdue, dueAtStr within (today, today + 7]. */
export function isComingUp(task, ctx) {
  const today = todayOf(ctx);
  if (!isOpen(task) || !today || isDueToday(task, ctx) || isOverdue(task, ctx)) return false;
  const due = dayOf(task.dueAtStr, ctx, false);
  return !!due && due > today && due <= addDays(today, COMING_UP_DAYS);
}

/** The completion day: completedAtStr (TodoItem stamps the real day) else completedAt. */
function completedOf(task, ctx) {
  const byStr = dateOf(task.completedAtStr, ctx, false);
  const byAt = dateOf(task.completedAt, ctx, false);
  if (byStr) return { ymd: byStr.ymd, minutes: byAt && byAt.ymd === byStr.ymd ? byAt.minutes : null };
  return byAt;
}

/** COMPLETED and completed on ctx.today. */
export function isDoneToday(task, ctx) {
  const today = todayOf(ctx);
  if (!isObj(task) || task.status !== 'COMPLETED' || !today) return false;
  const c = completedOf(task, ctx);
  return !!c && c.ymd === today;
}

/** TodoItem `isCreatedToday` ("New today" badge): createdAt falls on ctx.today. */
export function isNewToday(task, ctx) {
  const today = todayOf(ctx);
  if (!isObj(task) || !today || !task.createdAt) return false;
  return dayOf(task.createdAt, ctx, false) === today;
}

// ---- Lists -----------------------------------------------------------------------------------

/**
 * The store getter's `_.uniqBy([...a, ...b, ...c], 'id')`: one task per id, FIRST occurrence wins,
 * in first-seen order. (completeTask.js combineStoreTasks keeps the LAST copy at the first
 * position — the store pushes the SAME object into each list, so both give the same tasks.)
 * Non-objects and id-less tasks are skipped.
 */
export function combineTasks(...lists) {
  const out = [];
  const seen = {};
  lists.forEach((list) => {
    arr(list).forEach((t) => {
      if (!isObj(t) || t.id === undefined || t.id === null || t.id === '') return;
      const k = String(t.id);
      if (seen[k]) return;
      seen[k] = true;
      out.push(t);
    });
  });
  return out;
}

function inputOf(input) {
  const i = isObj(input) ? input : {};
  return {
    myCreated: arr(i.myCreated),
    assignedToMe: arr(i.assignedToMe),
    myEmployees: arr(i.myEmployees),
    allSite: arr(i.allSite),
    completedSite: arr(i.completedSite),
  };
}

function stableSort(list, cmp) {
  return list.map((t, i) => ({ t, i })).sort((a, b) => cmp(a.t, b.t) || a.i - b.i).map(x => x.t);
}

const byDueAsc = ctx => (a, b) => {
  const da = dueDayOf(a, ctx) || '9999-99-99';
  const db = dueDayOf(b, ctx) || '9999-99-99';
  return da < db ? -1 : (da > db ? 1 : 0);
};

/** Newest completion first; unknown times last; ties keep input order. */
const byDoneDesc = ctx => (a, b) => {
  const ca = completedOf(a, ctx);
  const cb = completedOf(b, ctx);
  const ka = ca ? `${ca.ymd} ${ca.minutes == null ? '    ' : String(10000 + ca.minutes)}` : '';
  const kb = cb ? `${cb.ymd} ${cb.minutes == null ? '    ' : String(10000 + cb.minutes)}` : '';
  return ka > kb ? -1 : (ka < kb ? 1 : 0);
};

/**
 * The rows of one sidebar view:
 *   myDay          the getter todaysTasksForEmployee(userID), in its order
 *   assignedToMe   open, assignees[] or assignedToEmpID is me (ToDo's assignee match) — a superset
 *                  of state.assignedToMeTasks for non-admins, whose fetch reads assignees[] only
 *   createdByMe    open, createdByEmpID is me (== state.myCreatedTasks)
 *   upcoming       mine (getter ownership), open, isComingUp — sorted by due day
 *   everyone       open and not deleted, every list (Admin / Super Admin fetch the whole site)
 *   completedToday completedSite, done today (anyone), newest first
 * Unknown view → [].
 */
export function tasksForView(view, input, ctx) {
  const i = inputOf(input);
  const c = ctx || {};
  const open = combineTasks(i.myCreated, i.assignedToMe, i.myEmployees);
  const me = c.userID;
  switch (view) {
    case 'myDay':
      return open.filter(t => isMyDayTask(t, c));
    case 'assignedToMe':
      return open.filter(t => isOpen(t) && me != null && assigneeIDs(t).some(id => String(id) === String(me)));
    case 'createdByMe':
      return open.filter(t => isOpen(t) && me != null && t.createdByEmpID === me);
    case 'upcoming':
      return stableSort(open.filter(t => isMine(t, me) && isComingUp(t, c)), byDueAsc(c));
    case 'everyone':
      return combineTasks(i.myCreated, i.assignedToMe, i.myEmployees, i.allSite)
        .filter(t => isOpen(t) && t.deleted !== true);
    case 'completedToday':
      return stableSort(combineTasks(i.completedSite).filter(t => isDoneToday(t, c)), byDoneDesc(c));
    default:
      return [];
  }
}

/** { myDay, assignedToMe, createdByMe, upcoming, everyone, completedToday } → row counts. */
export function viewCounts(input, ctx) {
  const out = {};
  VIEW_KEYS.forEach((k) => { out[k] = tasksForView(k, input, ctx).length; });
  return out;
}

/**
 * Split a list into the My day sections (each task lands in at most one):
 *   today      open && isDueToday (input order — for My day this IS the getter's list)
 *   overdue    open && isOverdue && not due today — oldest due first ("Oldest from …")
 *   comingUp   isComingUp — soonest first (show COMING_UP_SHOWN, "See all N in Upcoming")
 *   doneToday  COMPLETED today — newest first
 * Ownership is the caller's: pass myDayScope(…) for My day.
 */
export function sections(tasks, ctx) {
  const today = [];
  const overdue = [];
  const comingUp = [];
  const doneToday = [];
  arr(tasks).forEach((t) => {
    if (!isObj(t)) return;
    if (t.status === 'COMPLETED') {
      if (isDoneToday(t, ctx)) doneToday.push(t);
      return;
    }
    if (!isOpen(t)) return;
    if (isDueToday(t, ctx)) today.push(t);
    else if (isOverdue(t, ctx)) overdue.push(t);
    else if (isComingUp(t, ctx)) comingUp.push(t);
  });
  return {
    overdue: stableSort(overdue, byDueAsc(ctx)),
    today,
    comingUp: stableSort(comingUp, byDueAsc(ctx)),
    doneToday: stableSort(doneToday, byDoneDesc(ctx)),
  };
}

/**
 * The tasks My day is built from: my open tasks (getter ownership, store order) then my tasks
 * completed today from completedSite (mine, or completed by me). One per id, open copy first.
 */
export function myDayScope(input, ctx) {
  const i = inputOf(input);
  const me = ctx && ctx.userID;
  const open = combineTasks(i.myCreated, i.assignedToMe, i.myEmployees).filter(t => isMine(t, me));
  const done = i.completedSite.filter(t => isObj(t) && (isMine(t, me) || (me != null && t.completedByEmpID === me)));
  return combineTasks(open, done);
}

/** sections(myDayScope(input, ctx), ctx). */
export function myDaySections(input, ctx) {
  return sections(myDayScope(input, ctx), ctx);
}

/**
 * The other views' grouping (design: Overdue · Today · This week · Later · No due date):
 *   overdue / today as in sections(); thisWeek = due in 1–6 days; later = 7+ days;
 *   noDate = no due day at all. COMPLETED rows go to `done` (any day).
 */
export function groupList(tasks, ctx) {
  const g = { overdue: [], today: [], thisWeek: [], later: [], noDate: [], done: [] };
  const today = todayOf(ctx);
  arr(tasks).forEach((t) => {
    if (!isObj(t)) return;
    if (t.status === 'COMPLETED') { g.done.push(t); return; }
    if (isDueToday(t, ctx)) { g.today.push(t); return; }
    if (isOverdue(t, ctx)) { g.overdue.push(t); return; }
    const due = dueDayOf(t, ctx);
    if (!due || !today) g.noDate.push(t);
    else if (due < today) g.overdue.push(t);
    else if (due < addDays(today, 7)) g.thisWeek.push(t);
    else g.later.push(t);
  });
  g.overdue = stableSort(g.overdue, byDueAsc(ctx));
  g.thisWeek = stableSort(g.thisWeek, byDueAsc(ctx));
  g.later = stableSort(g.later, byDueAsc(ctx));
  g.done = stableSort(g.done, byDoneDesc(ctx));
  return g;
}

/**
 * The header ring: done = doneToday, left = today (overdue is NOT counted), total = done + left,
 * pct = rounded %, since = minutes after midnight of the earliest completion today, or null.
 * ⚠ tasks/updateTask stamps completedAt = start of the office day, so `since` is 0 (12:00 AM) for
 *   every task completed from the list — there is no real completion time to show yet.
 */
export function progress(sectionsResult, ctx) {
  const s = isObj(sectionsResult) ? sectionsResult : {};
  const done = arr(s.doneToday).length;
  const left = arr(s.today).length;
  const total = done + left;
  let since = null;
  arr(s.doneToday).forEach((t) => {
    if (!isObj(t)) return;
    const c = completedOf(t, ctx);
    if (c && c.minutes != null && (since === null || c.minutes < since)) since = c.minutes;
  });
  return { done, total, left, pct: total ? Math.round((done / total) * 100) : 0, since };
}

// ---- Sidebar ---------------------------------------------------------------------------------

const byLabel = (a, b) => String(a.label).localeCompare(String(b.label));

/**
 * TEAM: one row per employee { key: String(EmployeeID), empID, label: Name, count, isMe } where
 * count = OPEN tasks the person is an assignee of (assigneeIDs). You first, then busiest, then
 * A–Z. Employees without an EmployeeID are skipped; tasks assigned to someone not in `employees`
 * are not listed.
 */
export function teamCounts(tasks, employees, ctx) {
  const counts = {};
  combineTasks(tasks).forEach((t) => {
    if (!isOpen(t)) return;
    assigneeIDs(t).forEach((id) => { counts[String(id)] = (counts[String(id)] || 0) + 1; });
  });
  const me = ctx && ctx.userID != null ? String(ctx.userID) : null;
  const seen = {};
  const rows = [];
  arr(employees).forEach((e) => {
    if (!isObj(e) || e.EmployeeID === undefined || e.EmployeeID === null || e.EmployeeID === '') return;
    const key = String(e.EmployeeID);
    if (seen[key]) return;
    seen[key] = true;
    rows.push({ key, empID: e.EmployeeID, label: e.Name || '', count: counts[key] || 0, isMe: key === me });
  });
  return rows.sort((a, b) => (b.isMe - a.isMe) || (b.count - a.count) || byLabel(a, b));
}

/**
 * TAGS: { key: tag, label: tag, count } — count = OPEN tasks carrying the tag in `tags` (trimmed,
 * once per task). Site tags (state.tasks.siteTaskTags, strings) nobody uses are listed with 0.
 * Busiest first, then A–Z.
 */
export function tagCounts(tasks, siteTags) {
  const counts = {};
  combineTasks(tasks).forEach((t) => {
    if (!isOpen(t)) return;
    const mine = {};
    arr(t.tags).forEach((g) => {
      if (typeof g !== 'string' || !g.trim() || mine[g.trim()]) return;
      mine[g.trim()] = true;
      counts[g.trim()] = (counts[g.trim()] || 0) + 1;
    });
  });
  arr(siteTags).forEach((g) => {
    if (typeof g === 'string' && g.trim() && counts[g.trim()] === undefined) counts[g.trim()] = 0;
  });
  return Object.keys(counts)
    .map(k => ({ key: k, label: k, count: counts[k] }))
    .sort((a, b) => (b.count - a.count) || byLabel(a, b));
}

/**
 * The first `n` rows of an already sorted teamCounts / tagCounts list, skipping count-0 rows
 * (except your own); the row whose key is `pinnedKey` (the person / tag being viewed) is appended
 * when it did not make the cut.
 */
export function busiest(list, n, pinnedKey) {
  const rows = arr(list).filter(isObj);
  const shown = rows.filter(r => r.isMe || r.count > 0).slice(0, Math.max(0, Number(n) || 0));
  if (pinnedKey !== undefined && pinnedKey !== null && pinnedKey !== '') {
    const k = String(pinnedKey);
    if (!shown.some(r => String(r.key) === k)) {
      const p = rows.find(r => String(r.key) === k);
      if (p) shown.push(p);
    }
  }
  return shown;
}

// ---- Content, search, row meta ---------------------------------------------------------------

const ENTITIES = { nbsp: ' ', amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", '#39': "'" };
function decode(s) {
  return s.replace(/&(#x[0-9a-f]+|#\d+|[a-z]+\d*);/gi, (m, e) => {
    const k = e.toLowerCase();
    if (ENTITIES[k] !== undefined) return ENTITIES[k];
    if (k[0] === '#') {
      const code = k[1] === 'x' ? parseInt(k.slice(2), 16) : parseInt(k.slice(1), 10);
      return Number.isFinite(code) ? String.fromCharCode(code) : m;
    }
    return m;
  });
}
const textOfHtml = s => decode(String(s).replace(/<!--[\s\S]*?-->/g, '').replace(/<[^>]*>/g, ''));

const BADGE_RE = /<span\b([^>]*)>((?:(?!<\/?span\b)[\s\S])*)<\/span>/gi;
const attr = (attrs, name) => {
  const m = new RegExp(`\\b${name}\\s*=\\s*(?:"([^"]*)"|'([^']*)')`, 'i').exec(attrs);
  return m ? decode(m[1] !== undefined ? m[1] : m[2]) : null;
};

/**
 * completeTask.js cleanTaskContent without a DOM: the task HTML → { text, employees, patients,
 * tags }. A <span class="… badge …"> with data-teamio-employeeid → employees, with
 * data-teamio-patientid → patients, any other badge → tags; badges leave the text.
 * Agreement-tested against cleanTaskContent(content, jsdom document).
 */
export function parseTaskContent(content) {
  const result = { text: '', employees: [], patients: [], tags: [] };
  if (content === undefined || content === null) return result;
  const html = String(content);
  const stripped = html.replace(BADGE_RE, (whole, attrs, inner) => {
    const cls = attr(attrs, 'class');
    if (cls === null || cls.split(/\s+/).indexOf('badge') === -1) return whole;
    const name = textOfHtml(inner).trim();
    const emp = attr(attrs, 'data-teamio-employeeid');
    const pat = attr(attrs, 'data-teamio-patientid');
    if (emp !== null) result.employees.push({ id: emp, name });
    else if (pat !== null) result.patients.push({ id: pat, name });
    else if (name) result.tags.push(name);
    return ' ';
  });
  result.text = decode(stripped.replace(/<!--[\s\S]*?-->/g, '').replace(/<[^>]*>/g, ' ')).replace(/\s+/g, ' ').trim();
  return result;
}

function cleanOf(task, ctx) {
  const content = isObj(task) ? task.content : '';
  if (ctx && typeof ctx.clean === 'function') {
    try {
      const r = ctx.clean(content);
      if (isObj(r)) {
        return { text: String(r.text || ''), employees: arr(r.employees), patients: arr(r.patients), tags: arr(r.tags) };
      }
    } catch (e) {
      // fall through to the regex parser
    }
  }
  return parseTaskContent(content);
}

/** Every tag a task has: its `tags` plus content badges (ToDo.vue's tag filter). */
export function allTagsOf(task, ctx) {
  if (!isObj(task)) return [];
  const out = [];
  arr(cleanOf(task, ctx).tags).concat(arr(task.tags)).forEach((g) => {
    if (typeof g === 'string' && out.indexOf(g) === -1) out.push(g);
  });
  return out;
}

/**
 * Narrow a list by the sidebar pick and the search box (all optional, AND-ed):
 *   empID  the task's assignees (assignees[] ∪ assignedToEmpID), compared as strings
 *   tag    allTagsOf includes it
 *   q      case-insensitive substring of the cleaned text or a tagged patient's name
 */
export function filterBy(tasks, opts, ctx) {
  const o = isObj(opts) ? opts : {};
  const hasEmp = o.empID !== undefined && o.empID !== null && o.empID !== '';
  const hasTag = typeof o.tag === 'string' && o.tag !== '';
  const q = typeof o.q === 'string' ? o.q.trim().toLowerCase() : '';
  return arr(tasks).filter((t) => {
    if (!isObj(t)) return false;
    if (hasEmp && !assigneeIDs(t).some(id => String(id) === String(o.empID))) return false;
    if (hasTag && allTagsOf(t, ctx).indexOf(o.tag) === -1) return false;
    if (q) {
      const c = cleanOf(t, ctx);
      const hay = [c.text].concat(arr(c.patients).map(p => (isObj(p) ? p.name : ''))).join('\n').toLowerCase();
      if (hay.indexOf(q) === -1) return false;
    }
    return true;
  });
}

/** TodoItem getOrdinal. */
function ordinal(n) {
  if (n == null) return '';
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

/** TodoItem formatRepeatFrequency; '' unless the task has `repeat` AND a repeatFrequency (as the row shows it). */
export function repeatLabel(task) {
  if (!isObj(task) || !task.repeat || !task.repeatFrequency) return '';
  const frequency = task.repeatFrequency;
  switch (frequency) {
    case 'Daily': {
      let dailyText = 'Repeats daily';
      const excludedDays = [];
      if (task.excludeSaturday) excludedDays.push('Saturday');
      if (task.excludeSunday) excludedDays.push('Sunday');
      if (excludedDays.length > 0) dailyText += ` (excluding ${excludedDays.join(' and ')})`;
      return dailyText;
    }
    case 'Weekly':
      return task.repeatOn && DAY_NAMES_FULL[task.repeatOn] ? `Repeats weekly on ${DAY_NAMES_FULL[task.repeatOn]}` : 'Repeats weekly';
    case 'Bi-Weekly':
      return task.repeatOn && DAY_NAMES_FULL[task.repeatOn] ? `Repeats bi-weekly on ${DAY_NAMES_FULL[task.repeatOn]}` : 'Repeats bi-weekly';
    case 'Monthly':
      if (task.repeatOnLastDayOfMonth === 'yes') return 'Repeats last day of every month';
      return task.repeatOnDay ? `Repeats ${ordinal(task.repeatOnDay)} of every month` : 'Repeats monthly';
    case 'Yearly':
      if (task.repeatOnDay && task.repeatOnMonth) {
        const monthName = MONTHS[parseInt(task.repeatOnMonth, 10) - 1] || '';
        if (task.repeatOnLastDayOfMonth === 'yes') return `Repeats last day of ${monthName} every year`;
        return `Repeats every ${monthName} ${ordinal(task.repeatOnDay)}`;
      }
      return 'Repeats yearly';
    default:
      return `Repeats ${String(frequency).toLowerCase()}`;
  }
}

/**
 * Everything a row shows besides its title:
 *   text            cleaned title (badges removed)
 *   patients        [{ id, name }] tagged patient badges (TodoItem shows 2 + "+N")
 *   tags            the task's `tags` (as TodoItem lists them)
 *   subtasks        { done, total } or null when there are none
 *   repeatLabel     TodoItem's "Repeats …" text, '' when not repeating
 *   attachments     wikiPages.length (the only attachments a task has)
 *   comments        comments.length, or null — list tasks carry NO comments (only the detail fetch
 *                   tasks/getTaskDetails loads them), so null means "unknown", not zero
 *   unreadComments  ctx.userID is in unreadCommentsList
 *   dueDay / dueWeekday   'YYYY-MM-DD' / 'Mon', or null
 *   assigneeEmpID   assignees[0], else assignedToEmpID, else null; assigneeEmpIDs = all of them
 *   isNewToday / isRepeating / completedByEmpID
 */
export function rowMeta(task, ctx) {
  const t = isObj(task) ? task : {};
  const c = cleanOf(t, ctx);
  const subs = arr(t.subtasks);
  const due = dueDayOf(t, ctx);
  const ids = assigneeIDs(t);
  const me = ctx && ctx.userID;
  return {
    text: c.text,
    patients: arr(c.patients),
    tags: arr(t.tags).filter(g => typeof g === 'string'),
    subtasks: subs.length ? { done: subs.filter(s => isObj(s) && s.status === 'COMPLETED').length, total: subs.length } : null,
    repeatLabel: repeatLabel(t),
    attachments: arr(t.wikiPages).length,
    comments: Array.isArray(t.comments) ? t.comments.length : null,
    unreadComments: me != null && arr(t.unreadCommentsList).indexOf(me) > -1,
    dueDay: due,
    dueWeekday: due ? weekdayOf(due) : null,
    assigneeEmpID: ids.length ? ids[0] : null,
    assigneeEmpIDs: ids,
    isNewToday: isNewToday(t, ctx),
    isRepeating: isRepeating(t),
    completedByEmpID: t.completedByEmpID || null,
  };
}

// ---- Move all to today -----------------------------------------------------------------------

/**
 * The tasks/updateTask payloads for "Move all to today": one per open, overdue, NOT-due-today,
 * NON-repeating task (one per id):
 *   { id, dueAt: Date (start of ctx.today in ctx.timeZone), dueAtStr: 'MM/DD/YYYY' | 'DD/MM/YYYY',
 *     tags: <the task's own tags>, lastActivity?: ctx.now }
 * Dispatch each with tasks/updateTask — NOT tasks/updateMultipleTasks, which writes sortID only.
 * [] when ctx.today is missing.
 */
export function moveAllToTodayPatch(tasks, ctx) {
  const today = todayOf(ctx);
  if (!today) return [];
  const tz = (ctx && ctx.timeZone) || DEFAULT_TIME_ZONE;
  const startMs = startOfDayMs(today, tz);
  if (startMs === null) return [];
  const dueAtStr = formatYmd(today, dueAtStrFormat(tz));
  const now = ctx && ctx.now instanceof Date && !Number.isNaN(ctx.now.getTime()) ? ctx.now : null;
  return combineTasks(tasks)
    .filter(t => isOverdue(t, ctx) && !isDueToday(t, ctx) && !isRepeating(t))
    .map((t) => {
      const patch = { id: t.id, dueAt: new Date(startMs), dueAtStr, tags: Array.isArray(t.tags) ? t.tags.slice() : [] };
      if (now) patch.lastActivity = new Date(now.getTime());
      return patch;
    });
}

/**
 * ONE tasks/updateTask payload that moves a task to `ymd` ('YYYY-MM-DD') — the calendar's drag-to-day.
 * Same fields and the same rules as moveAllToTodayPatch: dueAt = start of that day in ctx.timeZone,
 * dueAtStr in the office's format, the task's own `tags` carried (updateTask writes `tags: []` when a
 * payload has none). null when nothing should be saved: not an object / no id, not open, REPEATING
 * (moving one re-anchors its whole series), a bad day, or the task is already due that day.
 */
export function rescheduleToDayPatch(task, ymd, ctx) {
  if (!isObj(task) || task.id === undefined || task.id === null || !isOpen(task) || isRepeating(task)) return null;
  const p = partsOfYmd(ymd);
  // partsOfYmd only checks the shape: 2026-02-31 would pass, and Date would roll it into March. Round-trip it.
  if (!p || ymdOfUtc(Date.UTC(p.y, p.m - 1, p.d)) !== ymd || dueDayOf(task, ctx) === ymd) return null;
  const tz = (ctx && ctx.timeZone) || DEFAULT_TIME_ZONE;
  const startMs = startOfDayMs(ymd, tz);
  if (startMs === null) return null;
  const now = ctx && ctx.now instanceof Date && !Number.isNaN(ctx.now.getTime()) ? ctx.now : null;
  const patch = { id: task.id, dueAt: new Date(startMs), dueAtStr: formatYmd(ymd, dueAtStrFormat(tz)), tags: Array.isArray(task.tags) ? task.tags.slice() : [] };
  if (now) patch.lastActivity = new Date(now.getTime());
  return patch;
}

// ---- Loading ---------------------------------------------------------------------------------

/** ToDo.vue fetchTasks: Super Admin / Admin load the whole site, everyone else their own tasks. */
export function isSiteWideRole(role) {
  return role === 'Super Admin' || role === 'Admin';
}

/** The store actions the page dispatches (each with { siteID, employeeID } / { siteID }). */
export function loadPlan(role) {
  return {
    tasks: isSiteWideRole(role) ? 'tasks/getTasksForSite' : 'tasks/getTasksForEmployee',
    completed: 'tasks/getCompletedTasksForSite',
    tags: 'tasks/subscribeSiteTaskTags',
  };
}
